import React, { useMemo, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Input from "@src/common/components/Input/Input";
import { LabelItem } from "./LabelItem";
import { Label } from "@api/graphql/generated/generated-types";
import {
  filterLabelsBySearchTerm,
  sortLabelsByName,
} from "../utils/labelFilters";

interface LabelSelectionSectionProps {
  labels: Label[];
  selectedLabelIds: string[];
  onLabelToggle: (labelId: string) => void;
  title: string;
  emptyMessage?: string;
  disabled?: boolean;
  searchable?: boolean;
}

export function LabelSelectionSection({
  labels,
  selectedLabelIds,
  onLabelToggle,
  title,
  emptyMessage = "No labels available.",
  disabled = false,
  searchable = false,
}: LabelSelectionSectionProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const sortedLabels = useMemo(() => sortLabelsByName(labels), [labels]);
  const visibleLabels = useMemo(
    () => filterLabelsBySearchTerm(sortedLabels, searchTerm),
    [sortedLabels, searchTerm]
  );

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{title}</div>
      {labels.length === 0 ? (
        <div className="text-gray-500 dark:text-gray-400 text-sm px-1">{emptyMessage}</div>
      ) : (
        <>
          {searchable && (
            <Input
              size="mini"
              placeholder="Search labels"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.currentTarget.value)}
              clearable
              startEnhancer={
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              }
            />
          )}
          {visibleLabels.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400 text-sm px-1">
              No labels match your search.
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {visibleLabels.map((label) => (
                <LabelItem
                  key={label.id}
                  label={label}
                  isChecked={selectedLabelIds.includes(label.id)}
                  onToggle={onLabelToggle}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
